// src/utils/fcm.js
import { messaging } from '../firebase';
import { getToken, onMessage } from 'firebase/messaging';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || 'BDffwRxjPqaf8bgkpDTh4ySY-2aRBo8Cvs_qR8HyvryT4feVGFMBoTMZ3HOm_wbKxCK-hslMh9sWyrSVpZETk1k';

async function getServiceWorkerRegistration() {
  try {
    const reg = await navigator.serviceWorker.getRegistration('/');
    if (reg) return reg;
    return navigator.serviceWorker.ready;
  } catch (err) {
    console.error('[FCM] Service worker registration failed:', err);
    return null;
  }
}

export const initializeFCM = async (user, setShowNotificationsPrompt, setHasPrompted) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[FCM] Initialization skipped in development');
    return;
  }

  if (!user || !('Notification' in window) || !messaging) {
    console.log('[FCM] Initialization skipped: user or notifications not available');
    return;
  }

  try {
    if (Notification.permission !== 'granted') {
      if (!localStorage.getItem('notificationPromptDismissed')) {
        setShowNotificationsPrompt(true);
      }
      return;
    }

    const swReg = await getServiceWorkerRegistration();
    if (!swReg) {
      console.error('[FCM] No service worker registration available');
      return;
    }

    let currentToken;
    try {
      currentToken = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg });
    } catch (err) {
      console.error('[FCM] Error getting token:', err);
      setShowNotificationsPrompt(false);
      setHasPrompted(true);
      localStorage.removeItem('fcmToken');
      localStorage.removeItem('fcmTokenSavedTimestamp');
      return;
    }

    if (!currentToken) {
      console.log('[FCM] No token available');
      setShowNotificationsPrompt(false);
      setHasPrompted(true);
      localStorage.removeItem('fcmToken');
      localStorage.removeItem('fcmTokenSavedTimestamp');
      return;
    }

    const savedToken = localStorage.getItem('fcmToken');
    const tokenSavedTimestamp = localStorage.getItem('fcmTokenSavedTimestamp');
    const oneDay = 24 * 60 * 60 * 1000;

    if (
      savedToken &&
      savedToken === currentToken &&
      tokenSavedTimestamp &&
      Date.now() - parseInt(tokenSavedTimestamp) < oneDay
    ) {
      console.log('[FCM] Using cached token:', savedToken);
      const isValid = await validateTokenWithBackend(currentToken);
      if (isValid) {
        setShowNotificationsPrompt(false);
        return;
      }
      console.log('[FCM] Cached token invalid, refreshing...');
    }

    console.log('[FCM] Saving token to backend:', currentToken);
    try {
      const response = await axios.post(
        `${API_URL}/api/save-fcm-token/`,
        { token: currentToken },
        { withCredentials: true }
      );
      if (response.data.success) {
        console.log('[FCM] Token saved to backend');
        localStorage.setItem('fcmToken', currentToken);
        localStorage.setItem('fcmTokenSavedTimestamp', Date.now().toString());
        setShowNotificationsPrompt(false);
        setHasPrompted(true);
      } else {
        console.error('[FCM] Backend save failed:', response.data.errors);
      }
    } catch (err) {
      console.error('[FCM] Error saving token to backend:', err);
    }
  } catch (err) {
    console.error('[FCM] Error initializing:', err);
    localStorage.removeItem('fcmToken');
    localStorage.removeItem('fcmTokenSavedTimestamp');
    setShowNotificationsPrompt(false);
    setHasPrompted(true);
  }
};

const validateTokenWithBackend = async (token) => {
  try {
    const response = await axios.post(
      `${API_URL}/api/validate-fcm-token/`,
      { token },
      { withCredentials: true }
    );
    return response.data.valid === true;
  } catch (err) {
    console.error('[FCM] Token validation failed:', err);
    return false;
  }
};

export const setupForegroundNotifications = (navigate) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[FCM] Foreground notifications skipped in development');
    return () => {};
  }

  if (!messaging) {
    console.log('[FCM] Messaging not available for foreground notifications');
    return () => {};
  }

  try {
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('[FCM] Foreground notification received:', payload);
      try {
        const notificationTitle = payload.notification?.title || 'SErraND Notification';
        const notificationOptions = {
          body: payload.notification?.body || 'You have a new notification from SErraND.',
          icon: '/logo192.png',
          data: payload.data || {},
        };
        const notification = new Notification(notificationTitle, notificationOptions);
        notification.onclick = (event) => {
          event.preventDefault();
          window.focus();
          if (notificationOptions.data.url) {
            navigate(notificationOptions.data.url);
          }
          notification.close();
        };
      } catch (err) {
        console.error('[FCM] Error displaying foreground notification:', err);
      }
    });
    return unsubscribe;
  } catch (err) {
    console.error('[FCM] Error setting up foreground notifications:', err);
    return () => {};
  }
};

export const syncFCMToken = async (user) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[FCM] Token sync skipped in development');
    return;
  }

  if (!user || !('Notification' in window) || !messaging || Notification.permission !== 'granted') {
    console.log('[FCM] Token sync skipped: conditions not met');
    return;
  }

  try {
    const swReg = await getServiceWorkerRegistration();
    if (!swReg) {
      console.error('[FCM] No service worker registration available');
      return;
    }

    let currentToken;
    try {
      currentToken = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg });
    } catch (err) {
      console.error('[FCM] Error getting token:', err);
      return;
    }

    if (!currentToken) {
      console.log('[FCM] No token available for sync');
      return;
    }

    const savedToken = localStorage.getItem('fcmToken');
    if (currentToken !== savedToken) {
      console.log('[FCM] Token changed, updating backend:', currentToken);
      try {
        const response = await axios.post(
          `${API_URL}/api/save-fcm-token/`,
          { token: currentToken },
          { withCredentials: true }
        );
        if (response.data.success) {
          localStorage.setItem('fcmToken', currentToken);
          localStorage.setItem('fcmTokenSavedTimestamp', Date.now().toString());
          console.log('[FCM] Token synced with backend');
        } else {
          console.error('[FCM] Backend sync failed:', response.data.errors);
        }
      } catch (err) {
        console.error('[FCM] Error syncing token to backend:', err);
      }
    } else {
      console.log('[FCM] Token unchanged, no sync needed');
    }
  } catch (err) {
    console.error('[FCM] Error syncing token:', err);
  }
};

export const startTokenHeartbeat = async (user) => {
  function stopHeartbeat() {
    console.log('[FCM] Heartbeat stopped');
  }

  if (process.env.NODE_ENV !== 'production') {
    console.log('[FCM] Heartbeat skipped in development');
    return stopHeartbeat;
  }

  if (!user || !('Notification' in window) || !messaging || Notification.permission !== 'granted') {
    console.log('[FCM] Heartbeat skipped: conditions not met');
    return stopHeartbeat;
  }

  const CHECK_MS = 1000 * 60 * 30; // 30 minutes
  let stopped = false;

  async function tick() {
    if (stopped) return;
    try {
      const swReg = await getServiceWorkerRegistration();
      if (!swReg) {
        console.error('[FCM] No service worker registration available');
        return;
      }

      let token;
      try {
        token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg });
      } catch (err) {
        console.error('[FCM] Heartbeat: Error getting token:', err);
        return;
      }

      if (!token) {
        console.log('[FCM] No token available for heartbeat');
        return;
      }

      const isValid = await validateTokenWithBackend(token);
      if (!isValid) {
        console.log('[FCM] Heartbeat: Token invalid, updating backend:', token);
        try {
          const response = await axios.post(
            `${API_URL}/api/save-fcm-token/`,
            { token },
            { withCredentials: true }
          );
          if (response.data.success) {
            localStorage.setItem('fcmToken', token);
            localStorage.setItem('fcmTokenSavedTimestamp', Date.now().toString());
            console.log('[FCM] Heartbeat: Token updated');
          } else {
            console.error('[FCM] Heartbeat: Backend save failed:', response.data.errors);
          }
        } catch (err) {
          console.error('[FCM] Heartbeat: Error saving token:', err);
        }
      }
    } catch (e) {
      console.error('[FCM] Heartbeat error:', e);
    }
    setTimeout(tick, CHECK_MS);
  }
  tick();
  return stopHeartbeat;
};

// Placeholder for compatibility
export const initializeFirebaseMessaging = async () => {
  console.log('[FCM] Initializing Firebase Messaging (no-op)');
};
